// Adapter layer for Patient Management.
//
// Contract: docs/api-contracts/home-dashboard.md §3 (GET /api/patients) & §4 (POST /api/patients/{uid}/sms)
//
// TODO: Replace mock implementations with real HTTP calls when backend is ready.
// The DTOs and function signatures are the stable contract — only swap the
// function bodies.

import { httpClient } from '@/api/http-client';
import { endpoints } from '@/api/endpoints';
import { mapAxiosError } from '@/api/error';
import type { ProFrequency, ProItem } from '@/features/patients/pro-items';
import type {
	ProAnswerRender,
	ProDayAnswer,
	ProDayResponse,
	ProGrade,
	ProResponseMap,
	ProWeekStatus,
} from '@/features/patients/pro-responses';
import type { BiometricData, HrBand } from '@/features/patients/bio-data';
// Server returns CSV blob (Content-Type: text/csv). Client only fetches + triggers download.
import { type InterventionItem } from '@/features/patients/intervention-data';
import type { ConsultationNote } from '@/features/patients/intervention-data';
// The consultation-manual types are defined below, against the wire the backend actually sends. They used to
// be imported from the mock module, which described the prototype's shape rather than the server's.

// ── Public Types ──

export const PatientStatus = {
	ACTIVE: 'ACTIVE',
	CLOSED: 'CLOSED',
	WITHDRAWN: 'WITHDRAWN',
} as const;

export type PatientStatus = (typeof PatientStatus)[keyof typeof PatientStatus];

/**
 * Is this patient's management over? True for a staff-ended record AND for a patient who withdrew from the
 * app themselves — two different ways of ending, one shared consequence for the screen.
 *
 * Exists so no screen has to spell the set out again. The shape it replaces — `status !== CLOSED`, meaning
 * "anything that is not that one value" — silently admitted every status added afterwards, so the day
 * WITHDRAWN started actually being written, controls that had been correct for months began appearing for
 * patients who had left. Ask the question positively, in one place, and adding a status forces a decision
 * here instead of quietly changing every screen at once.
 */
export function isManagementEnded(status: PatientStatus): boolean {
	return status === PatientStatus.CLOSED || status === PatientStatus.WITHDRAWN;
}

/** The mirror of {@link isManagementEnded}: still under care, so live controls and live values apply. */
export function isUnderManagement(status: PatientStatus): boolean {
	return status === PatientStatus.ACTIVE;
}

// Wire codes emitted by medical-api (Patient.risk_level; V75 rescale to the 0–26.8 R-value scale):
// LOW / MODERATE / HIGH, plus UNKNOWN (문진 미수행 → "산출 불가"). The G1/G2/G3 symbol names are kept as
// aliases used across the UI (G3 = highest risk = HIGH) so every existing call site resolves to the
// correct wire value. Previously these held 'G1'|'G2'|'G3' and never matched the wire → grade colour,
// the 주의/danger filter and 산출불가 were all dead. High-risk band = R ≥ 14.5 (admin R_DANGER_THRESHOLD).
export const RiskLevel = {
	G1: 'LOW',
	G2: 'MODERATE',
	G3: 'HIGH',
	UNKNOWN: 'UNKNOWN',
} as const;

export type RiskLevel = (typeof RiskLevel)[keyof typeof RiskLevel];

/**
 * One admin-configured R-index band boundary (SHCT-675).
 *
 * The boundaries live in the admin module — `ad_biometric_risk_band`, edited on 데이터기준값 > 위험지수(R)
 * and served by `GET/PUT /api/v1/admin/biometric-risk-bands`. PRD 11 §7.2 and PRD 04 §3.1 both say so in
 * as many words ("경계값은 어드민에서 관리"), and the R칩 popover is required to PRINT those boundaries.
 *
 * `min`/`max` are inclusive at both ends, matching how the admin service reads the same rows: a band's
 * `max` is a value belonging to that band, not the start of the next one.
 */
export interface RiskBand {
	band: Exclude<RiskLevel, 'UNKNOWN'>;
	min: number;
	max: number;
}

export const AppUser = {
	PATIENT: 'patient',
	GUARDIAN: 'guardian',
} as const;

export type AppUser = (typeof AppUser)[keyof typeof AppUser];

export interface PatientListItem {
	uid: string;
	mrn: string;
	name: string;
	gender: string;
	birthDate: string;
	phone: string;
	dischargeDate: string;
	primaryDxCode: string | null;
	primaryDxName: string;
	secondaryDx: string | null;
	secondaryDxName: string | null;
	riskScore: number | null;
	riskLevel: RiskLevel | null;
	/**
	 * SHCT-675 — the admin-configured band boundaries the R칩 popover prints. Carried on the DETAIL
	 * response only (the popover exists on one screen, and the boundaries are one global config rather
	 * than a per-row value), so a list row leaves it undefined. Absent until medical-api serves it; the
	 * popover then falls back to the seed boundaries and says so.
	 */
	riskBands?: RiskBand[];
	delayed: boolean;
	status: PatientStatus;
	appRegisteredAt: string | null;
	appUser: AppUser;
	guardian: { name: string; relationship: string; mobile: string } | null;
	address?: string;
	/**
	 * 상세 주소 — the hand-typed remainder of the address (a floor, a unit, a building name), held
	 * separately from the road-name value the address search returns (환자앱 01 §2.2.4; 어드민 02 §3.6).
	 * SHCT-915: the customer asked for it on this screen too, on its own line under the address row.
	 *
	 * THREE states, deliberately distinct — the header reads them apart:
	 *   · `undefined` — the response carries no such field: a backend that predates it. The row is not
	 *     drawn at all, rather than drawing a `-` for every patient in the hospital.
	 *   · `null` / `""` — the field is served and this patient left it blank → the header prints `-`.
	 *   · a string — printed as-is.
	 * So do NOT collapse null to undefined here the way `address` does; that would erase the difference
	 * between "the server does not send this yet" and "this patient has none".
	 */
	residenceDetail?: string | null;
	closedReason?: string; // "관리종료 · 재입원" | "관리종료 · 만기" (only for status "종료")
	/**
	 * 종료일 — the date management actually ended (staff-supplied on close; the patient-app self-withdraw
	 * path stamps the withdrawal date). Freezes the treatment badges at close time so "N주 후 종료" stops
	 * climbing (PRD 04 §3.1; BA 2026-07-29). Absent → the badges drop the week number rather than measure
	 * it against today. Cleared by revert-close, which restarts the clock. **Needs BE support** — see the
	 * BE hand-off note: `MedicalPatientItem` does not carry it yet even though `pt_patient.close_date` exists.
	 */
	closeDate?: string | null;
	medications?: { name: string; freq: string }[];
	primaryStaff?: string;
	/**
	 * The primary attending's staff id (SHCT-954). The edit modal matches on THIS, never on
	 * `primaryStaff` — two providers can share a name, and the staff selector returns ACTIVE staff
	 * only, so a Withdrawn (탈퇴) attending is absent from it and a same-named active provider used
	 * to be matched in their place. Absent for an unassigned patient (`primaryStaff` = "미배정").
	 */
	primaryStaffId?: number;
	/**
	 * The primary attending's department (부서), sent alongside the id so the chip can render a
	 * provider the staff selector omits. Without it a Withdrawn (탈퇴) attending renders "이름 ()".
	 */
	primaryStaffDepartment?: string;
	lastProDate?: string | null;
	/**
	 * Count of this-week 단계 3(G3) 위험 PRO responses (PRD 03 §2.3/§3.1). Drives the card's
	 * "단계 3 N" chip — shown only when > 0, hidden at 0/closed. BE-provided (TBD): until the
	 * server sends it this stays undefined and the chip is correctly hidden (no fabricated data).
	 */
	stage3Count?: number | null;
	/** Assisting care-team staff ids (strings) — pre-fills the edit modal's 담당 의료진 picker (PRD 03 §3.3). */
	careTeam?: string[];
}

export interface PatientFilters {
	search?: string;
	status?: string; // 'ALL' | 'active' | 'closed'
	riskLevel?: string; // '' | 'HIGH' | 'MODERATE' | 'LOW'
	primaryDx?: string; // admin ad_disease_group id, serialized as a string
	staff?: string; // assigned-staff email (담당의 filter, canViewAll only) — BE `staff` param
	dischargeMonths?: string; // '' | '1' | '3' | '6' | '12'
	delayedOnly?: boolean;
	/**
	 * 주의 tab — server-side filter. The server keeps patients still under management whose risk band is
	 * 고위험 or 산출 불가; it deliberately does NOT use the home 위험군 panel's stored at-risk flag, whose extra
	 * resting-heart-rate arm used to pull 저위험 patients into this tab (SHCT-578).
	 */
	dangerOnly?: boolean;
	page?: number;
	size?: number;
	sortKey?: string;
	sortDir?: 'asc' | 'desc';
}

export interface PatientListResponse {
	data: PatientListItem[];
	total: number;
	page: number;
	size: number;
}

export interface SmsResponse {
	success: boolean;
}

/**
 * Per-tab patient-list totals (SHCT-198 BE-1 `GET /patients/counts`), care-team-scoped identically to the
 * list. Server-computed over the WHOLE scoped set, so the tab badges are accurate regardless of how far the
 * infinite-scroll list has loaded. `danger` = the 주의 tab: ACTIVE ∧ (HIGH ∨ UNKNOWN),
 * the same filter the list applies for `dangerOnly=true`, so the badge and the tab's own list always agree.
 * NOT the home 위험군 panel's rule — that one also counts an abnormal resting heart rate (SHCT-578).
 */
export interface PatientCounts {
	all: number;
	danger: number;
	delayed: number;
	closed: number;
}

// ── BE ↔ FE mapping (medical-api returns English CODES on the wire; the FE displays Korean) ──

// The shape medical-api actually returns (MedicalPatientItem): status / riskLevel / closedReason are CODES.
interface PatientItemWire {
	uid: string;
	mrn: string;
	name: string | null;
	gender: string | null;
	birthDate: string | null;
	phone: string | null;
	dischargeDate: string | null;
	primaryDxCode: string | null;
	primaryDxName: string | null;
	secondaryDx: string | null;
	secondaryDxName: string | null;
	riskScore: number | null;
	riskLevel: string | null; // LOW | MODERATE | HIGH | UNKNOWN
	/** SHCT-675 — admin-configured R band boundaries. Absent until the BE adds it to the response. */
	riskBands?: { band?: string | null; min?: number | null; max?: number | null }[] | null;
	delayed: boolean;
	status: string; // ACTIVE | CLOSED | WITHDRAWN
	appRegisteredAt: string | null;
	appUser: AppUser;
	guardian: { name: string; relationship: string; mobile: string } | null;
	address?: string | null;
	/**
	 * 상세 주소 (SHCT-915). Named as admin-api already names it on its own patient response, so the two
	 * services describe one column with one word. **Absent until medical-api adds it** — see the field's
	 * note on `PatientListItem` for why the absent case must stay distinguishable from an empty one.
	 */
	residenceDetail?: string | null;
	closedReason?: string | null; // COMPLETED | REHOSPITALIZED | WITHDRAWN | OTHER
	/** 종료일 (ISO date) — stored as `pt_patient.close_date`. Absent until the BE adds it to the response. */
	closeDate?: string | null;
	/**
	 * 처방약 rows as the server sends them. BOTH fields are nullable on the wire even though a saved
	 * row always has at least a drug name: BE `MedicationEntry` documents freq as "Optional — a drug
	 * may be recorded on its own", so a row saved with the 복약주기 box left blank comes back as
	 * `{ name: "…", freq: null }`. Declared honestly here (SHCT-840) — the old
	 * `{ name: string; freq: string }[]` claimed a guarantee the server never made, and
	 * `mapPatient` passed the nulls straight into the edit form, where the registration schema's
	 * `z.string()` rejected them and silently disabled [수정].
	 */
	medications?: { name?: string | null; freq?: string | null }[] | null;
	primaryStaff?: string | null;
	/** Primary attending's staff id + department (SHCT-954) — see the domain type above. The server
	 * also sends `primaryStaffStatus`; it is deliberately not mapped, because nothing renders it yet
	 * (marking a Withdrawn (탈퇴) attending on screen is a separate CR). */
	primaryStaffId?: number | null;
	primaryStaffDepartment?: string | null;
	lastProDate?: string | null;
	/** This week's 단계 3(G3) PRO response count — drives the card's "단계 3 N" chip (PRD 03 §2.3).
	 * BE-provided; null/absent → chip hidden (no fake data). Needs BE support (see BE handoff). */
	stage3Count?: number | null;
	/** Assisting care-team staff ids (strings). The edit modal pre-fills the 담당 의료진 picker from this
	 * (PRD 03 §3.3). Excludes the primary attending — symmetric with the register/edit `careTeam` input. */
	careTeam?: string[] | null;
}

interface ApiSuccess<T> {
	data: T;
}
interface PatientListWire {
	data: PatientItemWire[];
	total: number;
	page: number;
	size: number;
}

// Pass through the API status code — consumers compare against PatientStatus.ACTIVE / .CLOSED / .WITHDRAWN.
function mapStatus(code: string): PatientStatus {
	if (
		code === PatientStatus.ACTIVE ||
		code === PatientStatus.CLOSED ||
		code === PatientStatus.WITHDRAWN
	)
		return code;
	return PatientStatus.ACTIVE; // fallback for unknown codes
}

/**
 * SHCT-675 — the wire's R band rows, narrowed to the three the popover can actually print.
 *
 * A row is kept only when it names a known band and carries BOTH numeric bounds: the popover prints a
 * range, so a half-configured row has nothing to print and is dropped rather than rendered as a hole.
 * Anything that is not an array at all (an error envelope, a null, an object) yields undefined, which
 * the popover reads as "the server did not send boundaries" and answers with the seed fallback.
 */
function mapRiskBands(rows: PatientItemWire['riskBands']): RiskBand[] | undefined {
	if (!Array.isArray(rows)) return undefined;
	const known: RiskBand['band'][] = ['LOW', 'MODERATE', 'HIGH'];
	const bands = rows.flatMap((r) => {
		const band = known.find((b) => b === r?.band);
		if (!band || typeof r?.min !== 'number' || typeof r?.max !== 'number') return [];
		return [{ band, min: r.min, max: r.max }];
	});
	return bands.length > 0 ? bands : undefined;
}

function mapPatient(w: PatientItemWire): PatientListItem {
	return {
		uid: w.uid,
		mrn: w.mrn,
		name: w.name ?? '',
		gender: w.gender ?? '',
		birthDate: w.birthDate ?? '',
		phone: w.phone ?? '',
		dischargeDate: w.dischargeDate ?? '',
		primaryDxCode: w.primaryDxCode,
		primaryDxName: w.primaryDxName ?? '',
		secondaryDx: w.secondaryDx,
		secondaryDxName: w.secondaryDxName,
		riskScore: w.riskScore,
		riskLevel: w.riskLevel as RiskLevel | null,
		riskBands: mapRiskBands(w.riskBands),
		delayed: w.delayed,
		status: mapStatus(w.status),
		appRegisteredAt: w.appRegisteredAt,
		appUser: w.appUser,
		guardian: w.guardian,
		address: w.address ?? undefined,
		// Carried through UNTOUCHED — `?? undefined` here would flatten "not served yet" and "served,
		// but blank for this patient" into one value, and the header draws those two differently.
		residenceDetail: w.residenceDetail,
		closedReason: w.closedReason ?? undefined,
		closeDate: w.closeDate ?? undefined,
		// SHCT-840 — collapse the wire's nulls to '' so every row is the pair of strings
		// `PatientListItem.medications` promises. A blank 복약주기 is a legitimate saved state
		// (PRD 03 §3.2: 약명·복약주기 "모두 선택 입력(필수 아님)"), so it must survive a round-trip
		// through the edit form untouched — it is not an error to be surfaced, and no rule here
		// makes one field require the other.
		medications: w.medications?.map((m) => ({
			name: m.name ?? '',
			freq: m.freq ?? '',
		})),
		primaryStaff: w.primaryStaff ?? undefined,
		primaryStaffId: w.primaryStaffId ?? undefined,
		primaryStaffDepartment: w.primaryStaffDepartment ?? undefined,
		lastProDate: w.lastProDate ?? undefined,
		stage3Count: w.stage3Count ?? undefined,
		careTeam: w.careTeam ?? undefined,
	};
}

// FE filter status ("active"/"closed"/"ALL") → BE status CODE param.
function mapStatusParam(s: string | undefined): string | undefined {
	if (!s || s === 'ALL') return undefined;
	if (s === 'active') return 'ACTIVE';
	if (s === 'closed') return 'CLOSED';
	return s.toUpperCase();
}

// ── API Functions ──

export async function fetchPatients(
	filters: PatientFilters = {},
): Promise<PatientListResponse> {
	const {
		search,
		status,
		riskLevel,
		primaryDx,
		staff,
		dischargeMonths,
		delayedOnly,
		dangerOnly,
		page = 1,
		size = 50,
		sortKey,
		sortDir,
	} = filters;

	const params: Record<string, string | number | boolean> = { page, size };
	if (search) params.search = search;
	const statusParam = mapStatusParam(status);
	if (statusParam) params.status = statusParam;
	// BE accepts HIGH/MODERATE/LOW and UNKNOWN(산출 불가) — server-side filter (V83 widened the risk-band set).
	if (riskLevel) params.riskLevel = riskLevel;
	if (primaryDx) params.primaryDx = primaryDx; // BE accepts the ad_disease_group id
	if (staff) params.staff = staff; // 담당의 filter — assigned-staff email (canViewAll only)
	if (dischargeMonths) params.dischargeMonths = parseInt(dischargeMonths, 10);
	// 지연/주의 tabs → server-side filters: delayed = pro_delayed flag, danger = the 고위험/산출 불가 risk band.
	if (delayedOnly) params.delayedOnly = true;
	if (dangerOnly) params.dangerOnly = true;
	if (sortKey) params.sortKey = sortKey;
	if (sortDir) params.sortDir = sortDir;

	try {
		const { data } = await httpClient.get<ApiSuccess<PatientListWire>>(
			endpoints.patients,
			{ params },
		);
		return {
			data: data.data.data.map(mapPatient),
			total: data.data.total,
			page: data.data.page,
			size: data.data.size,
		};
	} catch (err) {
		throw mapAxiosError(err);
	}
}

/**
 * Fetch the per-tab list counts (SHCT-198 BE-1). One cheap call feeds all four tab badges with totals over
 * the full care-team-scoped set, replacing the old client-side count that only saw the loaded pages.
 */
export async function fetchPatientCounts(): Promise<PatientCounts> {
	try {
		const { data } = await httpClient.get<ApiSuccess<PatientCounts>>(
			endpoints.patientsCounts,
		);
		const c = data.data;
		return {
			all: c?.all ?? 0,
			danger: c?.danger ?? 0,
			delayed: c?.delayed ?? 0,
			closed: c?.closed ?? 0,
		};
	} catch (err) {
		throw mapAxiosError(err);
	}
}

/**
 * Download the patient-list CSV (FEATURE_013; patient-management PRD §7 "환자 리스트 CSV 다운로드"). The BE returns
 * the FULL filtered set (server-side, care-team-scoped, audit-logged) as a UTF-8 CSV blob — the client only
 * fetches it and triggers the browser download. Passes the same filters as {@link fetchPatients} EXCEPT page/size
 * (the export is never paginated). `delayedOnly` IS sent — the BE `/csv` endpoint applies it (same flag the list
 * uses), so the exported file matches the on-screen 지연-filtered list (PRD 02 §3.5.7 CSV = 현재 필터 적용 목록).
 */
export async function downloadPatientsListCsv(
	filters: PatientFilters = {},
): Promise<void> {
	const {
		search,
		status,
		riskLevel,
		primaryDx,
		staff,
		dischargeMonths,
		delayedOnly,
		dangerOnly,
		sortKey,
		sortDir,
	} = filters;

	const params: Record<string, string | number | boolean> = {};
	if (search) params.search = search;
	const statusParam = mapStatusParam(status);
	if (statusParam) params.status = statusParam;
	if (riskLevel) params.riskLevel = riskLevel;
	if (primaryDx) params.primaryDx = primaryDx;
	if (staff) params.staff = staff;
	if (dischargeMonths) params.dischargeMonths = parseInt(dischargeMonths, 10);
	if (delayedOnly) params.delayedOnly = true;
	if (dangerOnly) params.dangerOnly = true;
	if (sortKey) params.sortKey = sortKey;
	if (sortDir) params.sortDir = sortDir;

	try {
		const res = await httpClient.get(endpoints.patientsCsv, {
			params,
			responseType: 'blob',
		});
		const today = new Date();
		const stamp = `${today.getFullYear()}${String(today.getMonth() + 1).padStart(2, '0')}${String(today.getDate()).padStart(2, '0')}`;
		triggerBlobDownload(res.data as Blob, `환자리스트_${stamp}.csv`);
	} catch (err) {
		throw mapAxiosError(err);
	}
}

/**
 * Fetch one patient's detail by uid.
 *
 * `unmask` (default false) → the server returns name/phone MASKED. Two callers pass `unmask: true`:
 * the edit modal, which opens the registration form pre-filled with the patient's existing values and
 * cannot edit a masked value back to a real one (PRD 03 §3.3); and the patient-detail header, which
 * prints the patient's own 전화번호 in the clear because the phone intervention sits on that same screen
 * and a masked number cannot be dialled (PRD 04 §3.1, added in v2.30 — SHCT-893). The header takes only
 * the phone from that read and keeps name and MRN masked on screen from its own separate masked read.
 * `careTeam` (assisting-staff ids) is returned in both modes.
 */
export async function fetchPatientDetail(
	uid: string,
	unmask = false,
): Promise<PatientListItem> {
	try {
		const { data } = await httpClient.get<ApiSuccess<PatientItemWire>>(
			endpoints.patientByUid(uid),
			unmask ? { params: { unmask: true } } : undefined,
		);
		return mapPatient(data.data);
	} catch (err) {
		throw mapAxiosError(err);
	}
}

export async function sendSms(uid: string): Promise<SmsResponse> {
	try {
		const { data } = await httpClient.post<ApiSuccess<{ success: boolean }>>(
			endpoints.patientSms(uid),
		);
		return { success: data.data.success };
	} catch (err) {
		throw mapAxiosError(err);
	}
}

// ── App-install guide SMS (FEATURE_014) — the message a newly registered patient receives ──

/**
 * How the patient's most recent install-guide SMS ended.
 *
 * `status` is `null` when no guide was ever sent (a patient registered with no number on file);
 * `canResend` is false when there is no number to send to, so the resend control can be disabled
 * rather than offer an action that can only fail.
 */
export interface InstallSmsStatus {
	status: 'PENDING' | 'SENT' | 'FAILED' | null;
	canResend: boolean;
}

/**
 * Read how the install guide's last send ended.
 *
 * The server sends that SMS only after the registration has been committed, so registration itself
 * cannot report the outcome — the client registers, then asks here.
 */
export async function fetchInstallSmsStatus(
	uid: string,
): Promise<InstallSmsStatus> {
	try {
		const { data } = await httpClient.get<ApiSuccess<InstallSmsStatus>>(
			endpoints.patientInstallSms(uid),
		);
		return data.data;
	} catch (err) {
		throw mapAxiosError(err);
	}
}

/** Send the install guide again. `success: false` means the gateway refused this attempt too. */
export async function resendInstallSms(uid: string): Promise<SmsResponse> {
	try {
		const { data } = await httpClient.post<ApiSuccess<{ success: boolean }>>(
			endpoints.patientInstallSms(uid),
		);
		return { success: data.data.success };
	} catch (err) {
		throw mapAxiosError(err);
	}
}

// ── Patient edit / revert / close (FEATURE_015 / FEATURE_016) ──

/**
 * Fields editable via PUT /patients/{uid} (PRD 03 §3.3 + US-MPM-edit).
 *
 * BE gap (TBD_BE_EDIT_PATIENT_FIELDS_01): `patientPhone`, `birthDate`, `gender`,
 * `guardianName`, `guardianRelationship`, `guardianPhone`, and `careTeam` are defined
 * in the PRD / ticket but may not yet be accepted by the backend. These fields are
 * sent optimistically — the BE will ignore unknown keys until the API contract is
 * aligned.
 *
 * TODO(TBD_BE_MRN_REQUIRED): `mrn` is not editable per the ticket but the backend
 * currently requires it in the PUT payload. Remove `mrn` once the BE stops requiring
 * it on update.
 */
export interface PatientEditInput {
	name: string;
	/** Not editable — sent only because the BE currently requires it. See TBD_BE_MRN_REQUIRED. */
	mrn: string;
	dischargeDate: string;
	primaryDx: string; // MH | NE | CV | EN | MS
	secondaryDx?: string | null;
	medications?: { name: string; freq: string }[];
	patientPhone?: string;
	birthDate?: string;
	gender?: string;
	guardianName?: string;
	guardianRelationship?: string;
	guardianPhone?: string;
	/** Staff ids — at least one required; no duplicates (US-MPM-edit).
	 * BE gap (TBD_BE_EDIT_PATIENT_FIELDS_01): field may not yet be accepted by the backend.
	 * Sent optimistically — BE will ignore unknown keys until API contract is aligned. */
	careTeam?: string[];
}

export interface PatientEditResult {
	patient: PatientListItem;
	proRecalcRequired: boolean;
}

export async function editPatient(
	uid: string,
	input: PatientEditInput,
): Promise<PatientEditResult> {
	try {
		const { data } = await httpClient.put<
			ApiSuccess<{ patient: PatientItemWire; proRecalcRequired: boolean }>
		>(endpoints.patientByUid(uid), input);
		return {
			patient: mapPatient(data.data.patient),
			proRecalcRequired: data.data.proRecalcRequired,
		};
	} catch (err) {
		throw mapAxiosError(err);
	}
}

export async function revertPatient(uid: string): Promise<PatientListItem> {
	try {
		const { data } = await httpClient.post<ApiSuccess<PatientItemWire>>(
			endpoints.patientRevert(uid),
		);
		return mapPatient(data.data);
	} catch (err) {
		throw mapAxiosError(err);
	}
}

export async function closePatient(
	uid: string,
	reason: string,
	endDate?: string,
	memo?: string,
): Promise<PatientListItem> {
	try {
		const { data } = await httpClient.post<ApiSuccess<PatientItemWire>>(
			endpoints.patientClose(uid),
			// BE PatientCloseRequest binds `closeDate` (not endDate) — key must match or it arrives null.
			{ reason, closeDate: endDate, memo },
		);
		return mapPatient(data.data);
	} catch (err) {
		throw mapAxiosError(err);
	}
}

// ── Care-team staff selector (FEATURE_038) — GET /staff ──

export interface StaffOption {
	id: number;
	email: string;
	name: string;
	department: string | null;
	role: string; // DOCTOR | NURSE | CRC
	jobFunction: string | null;
}

export async function fetchStaff(role?: string): Promise<StaffOption[]> {
	const { data } = await httpClient.get<ApiSuccess<StaffOption[]>>(
		endpoints.staff,
		{
			params: role ? { role } : {},
		},
	);
	return data.data;
}

// ── Disease master (FEATURE_014) — GET /disease-groups + /disease-codes ──
// Keyed on the real admin ad_disease_group id (one entry per admin group), not a 5-domain code.
// fetchDiseaseCodes takes the group id serialized as a string (it is a URL query param).

export interface DiseaseGroupOption {
	id: number; // real ad_disease_group id — one entry per admin disease group (FEATURE_014)
	name: string;
	range: string;
}
export interface DiseaseCodeOption {
	code: string;
	name: string;
}

export async function fetchDiseaseGroups(): Promise<DiseaseGroupOption[]> {
	const { data } = await httpClient.get<ApiSuccess<DiseaseGroupOption[]>>(
		endpoints.diseaseGroups,
	);
	return data.data;
}

export async function fetchDiseaseCodes(
	group: string,
): Promise<DiseaseCodeOption[]> {
	const { data } = await httpClient.get<ApiSuccess<DiseaseCodeOption[]>>(
		endpoints.diseaseCodes,
		{ params: { group } },
	);
	return data.data;
}

// ── Register a new patient (FEATURE_014) — POST /patients ──
// Field names match the BE PatientRegisterRequest 1:1 (and the FE registration form values), so the form
// values post directly.
//
// BE gap (CONFLICT_MEDICAL_REGISTER_CARETEAM_01): `careTeam` is defined per PRD 03 §3.2 but the BE
// auto-assigns the registering staff and has no register field for care-team today. The FE sends it
// optimistically — the BE will ignore it until the API contract is aligned.
export interface PatientRegisterInput {
	appUser: string;
	name: string;
	mrn: string;
	dischargeDate: string;
	primaryDx: string;
	secondaryDx?: string;
	medications?: { name: string; freq: string }[];
	patientPhone?: string;
	birthDate?: string;
	gender?: string;
	guardianName?: string;
	guardianRelationship?: string;
	guardianPhone?: string;
	/** Staff ids to assign alongside the registering clinician (PRD 03 §3.2). The server adds the
	 * registering clinician itself as the primary attending, so this list need not name them; an id
	 * that names no staff member is rejected rather than dropped. */
	careTeam?: string[];
}

export async function registerPatient(
	input: PatientRegisterInput,
): Promise<PatientListItem> {
	try {
		const { data } = await httpClient.post<ApiSuccess<PatientItemWire>>(
			endpoints.patients,
			input,
		);
		return mapPatient(data.data);
	} catch (err) {
		throw mapAxiosError(err);
	}
}

// ── Create a schedule (FEATURE_023) — POST /schedules ──
export interface ScheduleCreateInput {
	patientValue: string; // patient uid (U-{id})
	scheduledAt: string; // ISO local datetime
	type: string; // VISIT | INPERSON | CALL | FU
	purpose: string;
	memo?: string;
	followUpOf?: number | null;
}
export async function createSchedule(
	input: ScheduleCreateInput,
): Promise<void> {
	try {
		await httpClient.post(endpoints.schedules, input);
	} catch (err) {
		throw mapAxiosError(err);
	}
}

// ── Edit a future schedule (FEATURE_025) — PUT /schedules/{id} ──
export interface ScheduleEditInput {
	scheduledAt: string; // ISO local datetime
	type: string; // VISIT | INPERSON | CALL | FU
	purpose: string;
	memo?: string;
}
export async function editSchedule(
	id: string | number,
	input: ScheduleEditInput,
): Promise<void> {
	try {
		await httpClient.put(endpoints.scheduleById(id), input);
	} catch (err) {
		throw mapAxiosError(err);
	}
}

// ── Cancel a future schedule (FEATURE_026) — DELETE /schedules/{id} ──
// PRD §3.4 Flow C: [일정 취소] removes the appointment and withdraws its patient notification (BE).
// Future scheduled rows only; own-assigned/소속 guarded server-side. Not recoverable.
export async function cancelSchedule(id: string | number): Promise<void> {
	try {
		await httpClient.delete(endpoints.scheduleById(id));
	} catch (err) {
		throw mapAxiosError(err);
	}
}

// ── Create an intervention note (FEATURE_022) — POST /patients/{uid}/interventions ──
export interface InterventionCreateInput {
	type: string; // message | call
	target?: string; // SELF | GUARDIAN
	channel?: string; // SMS | APP | SMS_APP
	callDirection?: string; // 방향 — OUT (발신) | IN (수신); BE ConsultationNote.callDirection
	callDuration?: number;
	title?: string;
	body: string;
	autoFlag?: boolean;
	occurredAt?: string;
	/**
	 * The appointment this record was started FROM — set only when the panel was opened via [중재 시작].
	 *
	 * PRD 04 v2.27 §3.4 [SHCT-599] ②-1: saving the record links it to that appointment
	 * (`Schedule.interventionId`), and **that link is the sole basis for 완료** — the server never infers
	 * completion from the time or the staff member. Omitted for a record written from the top-right
	 * [신규 중재], which by the same clause completes no appointment.
	 *
	 * The bare numeric id the timeline already strips out of the BE's `sched-<n>` (`stripInterventionId`),
	 * sent as-is: the server takes a Long and coerces it, so a value that is not a number fails visibly
	 * instead of arriving as null — which the server would read as "no link" and silently complete nothing,
	 * the exact failure this field exists to close.
	 */
	scheduleId?: string;
}
export async function createIntervention(
	uid: string,
	input: InterventionCreateInput,
): Promise<void> {
	try {
		await httpClient.post(endpoints.patientInterventions(uid), input);
	} catch (err) {
		throw mapAxiosError(err);
	}
}

// ── Edit an intervention note (FEATURE_022) — PUT /patients/{uid}/interventions/{noteId} ──
// Only 제목/내용 are editable; type/target/channel/duration/일시 are immutable by design (BE
// InterventionEditRequest). Author-only — BE returns INTERVENTION_NOT_AUTHOR for non-authors.
export interface InterventionEditInput {
	title?: string;
	body: string;
}
export async function editIntervention(
	uid: string,
	noteId: string,
	input: InterventionEditInput,
): Promise<void> {
	try {
		await httpClient.put(endpoints.patientInterventionById(uid, noteId), input);
	} catch (err) {
		throw mapAxiosError(err);
	}
}

// ── Download helper (blob → browser save) ──
function triggerBlobDownload(blob: Blob, filename: string): void {
	const url = URL.createObjectURL(blob);
	const a = document.createElement('a');
	a.href = url;
	a.download = filename;
	document.body.appendChild(a);
	a.click();
	a.remove();
	URL.revokeObjectURL(url);
}

// ── PRO data layer (BE: GET /pro-responses + /pro-responses/csv) ──

/** PRO area grouping (mirrors pro-items.ts ProArea). */
export interface ProAreaDTO {
	index: number;
	label: string;
	items: ProItem[];
}

export type { ProResponseMap };

/** Treatment-window bar above the PRO heatmap (BE ProHeatmapResponse.treatmentBar). */
export interface TreatmentBarDTO {
	/** Treatment start = discharge date (ISO yyyy-MM-dd); null if not discharged. */
	startDate: string | null;
	/** Window end = discharge + 6 months (ISO); null if not discharged. */
	maturityDate: string | null;
	/** 1-based index of the current treatment week (0 before start). */
	currentWeek: number;
	/** Total weeks in the window (26). */
	totalWeeks: number;
}

// ── The wire, exactly as the backend sends it (BE ProHeatmapResponse) ──

/** One square: one item, one week. `grade` is only present when `status` is GRADED. */
interface ProCellWire {
	/** 1-based week (the grid renders 0-based, so this is decremented on the way in). */
	week: number;
	/** Grade CODE `G1`/`G2`/`G3` — text, not a number. Null for every non-graded state. */
	grade: string | null;
	/** One of GRADED / COMPLETED / MISSED / NOT_SCHEDULED / FUTURE. */
	status: string | null;
}

interface ProItemRowWire {
	itemId: number;
	name: string;
	/** Cadence CODE — DAILY / WEEKLY / BIWEEKLY / MONTHLY / QUARTERLY, or "" when the item has no schedule. */
	freq: string | null;
	cells: ProCellWire[] | null;
}

interface ProAreaWire {
	/** Korean area name, from the admin-owned PRO master. */
	area: string | null;
	items: ProItemRowWire[] | null;
}

interface ProHeatmapWire {
	treatmentBar: TreatmentBarDTO | null;
	areas: ProAreaWire[] | null;
	weeks: number;
}

/**
 * The backend's cadence CODE → the frequency this app already speaks, and the Korean the clinician reads.
 *
 * The server deliberately sends a code and never the Korean word: that keeps one label in one place instead of
 * a copy inside every app that displays it. This map is that one place, on this side.
 */
const CADENCE: Record<string, { frequency: ProFrequency; label: string }> = {
	DAILY: { frequency: 'daily', label: '매일' },
	WEEKLY: { frequency: 'weekly', label: '주1회' },
	BIWEEKLY: { frequency: 'biweekly', label: '2주1회' },
	MONTHLY: { frequency: 'monthly', label: '월1회' },
	QUARTERLY: { frequency: 'quarterly', label: '분기1회' },
};

/**
 * The backend's cell state → this app's own.
 *
 * Three of the backend's states look blank on screen and mean opposite things, so each is mapped on its own and
 * NONE of them is allowed to collapse into another:
 *  - MISSED        → the item was due and the patient did not answer. A clinician must see this.
 *  - NOT_SCHEDULED → nothing was ever due that week. Not a missed check-in, and must never read as one.
 *  - FUTURE        → the week has not arrived; the patient cannot be late for it.
 *  - COMPLETED     → the patient DID answer, but this item is pass/fail and has no score to give.
 *
 * COMPLETED is the one the frontend had no state for. Folding it into "answered with no grade" would render the
 * missed-chip (a dash) for a patient who actually answered — the precise confusion this whole grid exists to
 * prevent — so it gets a state of its own. See TBD_MEDICAL_PRO_CELL_STATE_COMPLETED_NOT_IN_SPEC_01.
 */
const CELL_STATUS: Record<string, ProWeekStatus> = {
	GRADED: 'responded',
	COMPLETED: 'completed',
	MISSED: 'missed',
	NOT_SCHEDULED: 'none',
	FUTURE: 'future',
};

/** `G1`/`G2`/`G3` (what the wire carries) → 1/2/3 (what this app's components take). */
function gradeOf(wireGrade: string | null): ProGrade | null {
	if (wireGrade === 'G1') return 1;
	if (wireGrade === 'G2') return 2;
	if (wireGrade === 'G3') return 3;
	return null;
}

/**
 * The platform's EIGHT answer-type codes → the rendering each one gets here.
 *
 * The eight codes are shared by the patient's app, this app and the server, and no app may coin its own spelling —
 * a code one service invented is a code the others cannot read. So they are never re-spelled; they are translated
 * here, once.
 *
 * Several codes are drawn the same way, which is not a shortcut but what they are: picking one option, answering
 * Yes/No, and answering O/X all produce a single chosen label, so all three render as `single`. `PHOTO` also maps
 * to `single`, because a photo answer's content IS its photos, and those are drawn from the answer's own photo list
 * rather than from a text line.
 */
const ANSWER_TYPE: Record<string, ProAnswerRender> = {
	SHORT: 'single',
	YESNO: 'single',
	OX: 'single',
	PHOTO: 'single',
	MULTI: 'multi',
	TEXT: 'text',
	SLIDER: 'slider',
	BATTERY: 'battery',
};

/** The backend's day state → this app's. `RESPONDED` and `MISSED` mean opposite things and never merge. */
const DAY_STATUS: Record<string, ProDayResponse['status']> = {
	RESPONDED: 'responded',
	MISSED: 'missed',
	FUTURE_EXPECTED: 'future-expected',
};

/** Korean weekday names, indexed the way `Date.getDay()` indexes them (Sunday first). */
const WEEKDAY_KO = [
	'일요일',
	'월요일',
	'화요일',
	'수요일',
	'목요일',
	'금요일',
	'토요일',
];

/**
 * One answered question as the server sends it.
 *
 * The slider + battery fields are NOT new on the wire — the server has carried them since the PRO
 * answer-detail work; this adapter simply never declared them, so it dropped them on the floor and every
 * slider and battery answer fell through to the plain-text branch. That is SHCT-741 Issue 2: the
 * renderers were built and waiting, and nothing was reaching them.
 *
 * `sliderValue` is a STRING on the wire (the raw stored `chosen_value`), not a number.
 *
 * There is no `multi` field and there does not need to be. A multi-select question arrives as **one entry
 * per selected option**, all sharing the same `questionQid` — the patient's app submits each selection as
 * its own answer, `ProSessionWriter` writes each as its own `pt_pro_response` row, and `answersOf` maps
 * each row straight through. The day's own total confirms the shape rather than merely allowing it: the
 * server sums every row's score, so a day whose Q-0128 selections score 8+8+3+5 reports 24.
 *
 * An earlier note here said the selections were flattened into one separator-less label and that splitting
 * them would be guessing. That was wrong — nothing is joined, so nothing has to be split. See
 * `groupMultiSelections`.
 */
interface ProDayAnswerWire {
	questionQid: string | null;
	answerType: string | null;
	answerText: string | null;
	/**
	 * The body of a written answer (텍스트입력형), which the requirement defines as separate from `otherText`.
	 *
	 * The server sends it only for a TEXT answer, and on such an answer it sends `otherText` as null — the patient's
	 * app stores that one body in two columns, so both fields carrying it would draw the same sentence twice: once as
	 * the response, once as a marginal 기타 remark. That double-rendering is why a written answer used to appear on
	 * this screen labelled 기타 with no 응답 (서술) line at all.
	 */
	textValue: string | null;
	otherText: string | null;
	score: number | null;
	photos: string[] | null;
	sliderValue: string | null;
	sliderUnit: string | null;
	sliderMin: number | null;
	sliderMax: number | null;
	battery: ProBatterySubAnswerWire[] | null;
}

/** One battery (묶음 응답형) sub-answer. `itemName` is null when the admin question config cannot name it. */
interface ProBatterySubAnswerWire {
	categoryIdx: number | null;
	itemIdx: number | null;
	scaleIdx: number | null;
	score: number | null;
	itemName: string | null;
}

/**
 * The slider fields the gauge needs, or nothing at all for a non-slider answer (SHCT-741).
 *
 * `sliderValue` arrives as a string; without it there is no gauge to draw, so an unparsable one yields
 * nothing and the answer falls back to its stored label rather than to a gauge pinned at zero.
 *
 * The unit and the range come from the admin question's `sliderConfig` and are null whenever that config
 * is not wired for the question. They are passed through as-is — the renderer already treats a missing
 * range as 0~10 — so an unconfigured slider still shows the patient's real value.
 */
function sliderOf(a: ProDayAnswerWire) {
	if (a.sliderValue == null || a.sliderValue === '') return {};
	const value = Number(a.sliderValue);
	if (!Number.isFinite(value)) return {};
	return {
		sliderValue: value,
		sliderUnit: a.sliderUnit ?? undefined,
		sliderMin: a.sliderMin ?? undefined,
		sliderMax: a.sliderMax ?? undefined,
	};
}

/**
 * The battery sub-items and their average, or nothing for a non-battery answer (SHCT-741).
 *
 * Two things the server cannot always supply, handled here rather than invented:
 *
 * - **The sub-item name.** It lives in the admin question config and is null when that config cannot be
 *   read, so the label falls back to the stored position (`1-2`, category-item, 1-based) exactly as the
 *   server's own contract says it should. A made-up name would misattribute an answer to the wrong item.
 * - **The value.** A sub-answer carries both a `score` and a `scaleIdx`. `score` is the number that
 *   actually means something clinically — it is what the day's grade is summed from — so it is preferred,
 *   with `scaleIdx` as the fallback for a sub-item that does not score.
 *
 * **`scaleIdx` is what says the patient answered at all, and `score` cannot stand in for it (SHCT-884).**
 * `scaleIdx` is the point they chose on the shared scale; a sub-item they never touched has none. `score`
 * is not a usable substitute because **0 is a legitimate score**: the self-efficacy battery is reverse-coded
 * (① 전혀 그렇지 않다 = 2 · ② 그렇지 않다 = 1 · ③ 그렇다 = 0 · ④ 매우 그렇다 = 0, patient PRD 03 §2.5.16), so a
 * patient who answered ③ or ④ — the two BEST answers — stores exactly the 0 an untouched slot used to store.
 * Reading `score` first therefore admitted the blanks: a build of the patient's app older than 2026-08-17
 * wrote one sub-answer per slot with `score: 0` and no `scaleIdx`, and every one of them was listed and
 * averaged as if answered. That is what a tester read as "값이 0" and reported as corrupt data.
 *
 * The average is computed over the values that survived, matching what is listed underneath it — so a
 * partly-filled battery now averages only the sub-items the patient actually answered, and the count in the
 * heading says how many that was.
 */
function batteryOf(a: ProDayAnswerWire) {
	if (!a.battery?.length) return {};
	const battery = a.battery
		.map((s) => {
			// No point on the scale = a slot the patient never answered. Dropped, not drawn as a 0.
			if (s.scaleIdx == null) return null;
			const value = s.score ?? s.scaleIdx;
			if (value == null) return null;
			return {
				label: s.itemName ?? `${(s.categoryIdx ?? 0) + 1}-${(s.itemIdx ?? 0) + 1}`,
				value,
			};
		})
		.filter((s): s is { label: string; value: number } => s !== null);
	if (!battery.length) return {};
	const batteryAvg =
		battery.reduce((sum, s) => sum + s.value, 0) / battery.length;
	return { battery, batteryAvg };
}

/**
 * One answer as this app models it, before grouping — the direct 1:1 translation of a wire row.
 *
 * With one correction, for written (`TEXT`) answers: **the body arrives in `otherText`, not `answerText`.**
 * The patient's app submits a written answer as `other_text` + `chosen_value` and never sets `answer_label`
 * (`proMapper.buildAnswer`, the `TEXT` arm), the server stores those columns verbatim, and the medical read
 * maps this DTO's `answerText` from `answer_label` alone — `chosen_value` is put on the wire only for
 * sliders. So a written answer reaches this adapter with an empty `answerText` and its body sitting in
 * `otherText`. Left as-is the popover drew it exactly wrong: the 서술 box was skipped (its renderer guards on
 * `answerText`) and the body printed in the amber 기타 block instead, which is what QC saw and reported as
 * "text renders as other response" (SHCT-741).
 *
 * The move is safe rather than a guess about which field meant what: a `TEXT` question has no options, so it
 * has no 기타 remark to carry, and the app writes nothing else into `other_text` for one. `otherText` is
 * cleared in the same step, so the body is shown once — as the response — instead of twice.
 *
 * **That day has come, so the interim remap is gone.** PRD 04 §3.2 specifies the written body as `textValue`,
 * "otherText와 별개" (separate from otherText), and the server now sends exactly that (`ProDayAnswer.textValue`,
 * read from `chosen_value` with `other_text` as its own fallback for rows predating that column). So the body is
 * taken from `textValue`, and `answerText` is left holding what it actually means — the option label, which a
 * written answer does not have.
 *
 * The `otherText` fallback below is kept for one reason only: a medical web deployed ahead of its backend still
 * has to render, and on that older shape the body arrives in `otherText` and nowhere else. It is a deployment
 * cushion, not the contract.
 */
function answerOf(a: ProDayAnswerWire): ProDayAnswer {
	const answerType = ANSWER_TYPE[a.answerType ?? ''] ?? 'single';
	const written = answerType === 'text';
	// The body: its own field when the server sends it, otherwise the deployment cushion described above.
	const body = written ? (a.textValue ?? a.otherText ?? '') : (a.textValue ?? '');
	// `otherText` is emptied ONLY where it WAS the body — the cushion path. If the server sends the body itself AND
	// an `otherText`, both are shown: a value the server deliberately carried is not this adapter's to discard, and
	// the requirement calls the two fields 별개 (separate), so they are allowed to coexist.
	const otherTextWasTheBody = written && !a.textValue && !!a.otherText;
	return {
		questionQid: a.questionQid ?? '',
		answerType,
		// Left as the option label the patient chose. A written answer has none, so this stays empty for one — the
		// earlier interim copied the body in here, which the renderer no longer needs and which made `answerText`
		// mean two different things depending on the answer type.
		answerText: a.answerText ?? '',
		textValue: body,
		// Emptied on the cushion path so the body is shown once — as the response — instead of twice, once as the
		// response and once as a remark the patient never wrote.
		otherText: otherTextWasTheBody ? '' : (a.otherText ?? ''),
		score: a.score ?? undefined,
		photos: (a.photos ?? []).map((url) => ({ url })),
		...sliderOf(a),
		...batteryOf(a),
	};
}

/**
 * Collapse a multi-select question's rows into the single answer the popover draws as chips (SHCT-741 Issue 1).
 *
 * The server sends one row per selected option, all carrying the same `questionQid`. Rendered as they arrive
 * that reads as several separate single-choice questions — which is exactly what QC saw — when it is one
 * question the patient answered several ways. So the MULTI rows of a qid become ONE answer whose `multi`
 * holds each selection's label, in the order the server sent them (it sorts by row id, i.e. as answered).
 *
 * Only `MULTI` rows are grouped, and only when the qid is non-empty: a blank qid cannot prove two rows
 * belong to the same question, and merging on that guess would fuse two questions' answers into one.
 * Every other answer type passes through untouched, one row one answer, because for those a repeated qid
 * would mean something this app has no rule for.
 *
 * The grouped answer's `score` is the SUM of its selections, matching how the server totals the day
 * (`MedicalProService.totalScore` adds every row); `otherText` takes the first 기타 remark present, and
 * photos concatenate, since a per-option attachment belongs to the question as a whole.
 */
function groupMultiSelections(wire: ProDayAnswerWire[]): ProDayAnswer[] {
	const out: ProDayAnswer[] = [];
	const multiIndexByQid = new Map<string, number>();

	for (const row of wire) {
		const answer = answerOf(row);
		const qid = answer.questionQid;
		if (answer.answerType !== 'multi' || qid === '') {
			out.push(answer);
			continue;
		}
		const existingIndex = multiIndexByQid.get(qid);
		if (existingIndex === undefined) {
			multiIndexByQid.set(qid, out.length);
			out.push({
				...answer,
				// The chips ARE the answer now; the per-row label would otherwise also print above them
				// as if it were the whole response, naming one selection and hiding the rest.
				answerText: '',
				multi: answer.answerText ? [answer.answerText] : [],
			});
			continue;
		}
		const group = out[existingIndex];
		if (answer.answerText) group.multi = [...(group.multi ?? []), answer.answerText];
		if (answer.score != null) group.score = (group.score ?? 0) + answer.score;
		if (!group.otherText && answer.otherText) group.otherText = answer.otherText;
		if (answer.photos?.length) {
			group.photos = [...(group.photos ?? []), ...answer.photos];
		}
	}
	return out;
}

interface ProDayDetailWire {
	date: string | null;
	expected: boolean;
	status: string | null;
	grade: string | null;
	score: number | null;
	answers: ProDayAnswerWire[] | null;
}

interface ProCellDetailWire {
	itemId: number;
	name: string | null;
	freq: string | null;
	week: number;
	weekStart: string | null;
	weekEnd: string | null;
	days: ProDayDetailWire[] | null;
}

/**
 * The day-by-day detail behind one heatmap square — what the popover shows when a clinician clicks a cell.
 *
 * A separate call on purpose: the grid is 18 items x 26 weeks, so carrying every day of every square in the grid's
 * own response would ship hundreds of answers to draw a panel that shows one of them.
 *
 * **Nothing here is invented.** Every field is what the server stored; a day the server does not report is a day
 * this app does not draw. Before this existed the panel was filled by a random-number generator — showing a
 * clinician made-up answers for a real patient is far worse than showing them none.
 */
export async function fetchProCellDetail(
	patientUid: string,
	itemId: string,
	weekIndex: number,
): Promise<ProDayResponse[]> {
	const { data } = await httpClient.get<ApiSuccess<ProCellDetailWire>>(
		endpoints.patientProResponses(patientUid),
		// The grid counts weeks from 0; the server counts them from 1.
		{ params: { week: weekIndex + 1, item: itemId } },
	);
	return (data.data?.days ?? []).map((day) => {
		const date = day.date ?? '';
		return {
			label: date
				? (WEEKDAY_KO[new Date(`${date}T00:00:00`).getDay()] ?? '')
				: '',
			date,
			isExpected: day.expected,
			status: DAY_STATUS[day.status ?? ''] ?? 'missed',
			// The grade is frozen at submit time. A null one does NOT mean "no answer" — a pass/fail item is
			// answered and simply has no band — so the status, never the grade, decides whether a day was answered.
			level: gradeOf(day.grade),
			score: day.score ?? undefined,
			answers: groupMultiSelections(day.answers ?? []),
			// Kept for the shape's sake; the popover reads `answers`, never these.
			answerText: '',
			otherText: '',
		};
	});
}

/**
 * Fetch one PRO answer photo and return it as a `data:` base64 URI, ready for an `<img src>`.
 *
 * The URL the server hands back (`ProPhotoUrlSigner`, BE) is a care-team-gated PROXY endpoint, not a public
 * image link — it requires the same `Authorization: Bearer` header as every other API call. A plain `<img
 * src={url}>` can never send that header (the browser's own image loader has no way to attach one), so it
 * always 401s. Routing the fetch through `httpClient` instead attaches the token via the shared interceptor,
 * exactly like every other authenticated call this app makes.
 */
export async function fetchProPhoto(proxyUrl: string): Promise<string> {
	try {
		const { data, headers } = await httpClient.get<ArrayBuffer>(proxyUrl, {
			responseType: 'arraybuffer',
		});
		const contentType = headers['content-type'] || 'image/jpeg';
		let binary = '';
		const bytes = new Uint8Array(data);
		for (let i = 0; i < bytes.length; i++) {
			binary += String.fromCharCode(bytes[i]);
		}
		return `data:${contentType};base64,${btoa(binary)}`;
	} catch (err) {
		throw mapAxiosError(err);
	}
}

/** The full PRO heatmap payload: the treatment bar, the item master, and the response grid. */
export interface ProHeatmap {
	treatmentBar: TreatmentBarDTO | null;
	/** The 4 design areas and their items — from the ADMIN-OWNED master, via the backend. Never local. */
	areas: ProAreaDTO[];
	grid: ProResponseMap;
}

/**
 * PRO heatmap for a patient — the treatment bar, the item master AND the 18x26 grid, all from one call.
 *
 * The item rows come from the server, not from a list kept in this repo, because the PRO master is owned by the
 * admin app: an administrator can add an item or change its cadence, and a copy living here would quietly go
 * stale. Deriving both the rows and their cells from the SAME response also makes the ids line up by
 * construction — a grid keyed by one master and filled from another would silently render nothing at all.
 */
export async function fetchProHeatmap(patientUid: string): Promise<ProHeatmap> {
	const { data } = await httpClient.get<ApiSuccess<ProHeatmapWire>>(
		endpoints.patientProResponses(patientUid),
	);
	const wire = data.data;
	const grid: ProResponseMap = new Map();
	const areas: ProAreaDTO[] = (wire?.areas ?? []).map((area, areaIndex) => ({
		index: areaIndex,
		label: area.area ?? '',
		items: (area.items ?? []).map((row) => {
			const cadence = CADENCE[row.freq ?? ''];
			for (const cell of row.cells ?? []) {
				// The wire counts weeks from 1; the grid's columns are 0-based.
				const weekIndex = cell.week - 1;
				grid.set(`${row.itemId}:${weekIndex}`, {
					grade: gradeOf(cell.grade),
					status: CELL_STATUS[cell.status ?? ''] ?? 'none',
					weekStart: getWeekStartISO(
						wire?.treatmentBar?.startDate ?? null,
						weekIndex,
					),
					weekEnd: getWeekEndISO(
						wire?.treatmentBar?.startDate ?? null,
						weekIndex,
					),
					week: weekIndex,
					// The server's heatmap carries no day-by-day breakdown — that needs its own endpoint, which is
					// specified but not built. An empty list is the honest answer; inventing days for a clinician
					// to read would be far worse than showing none. TBD_MEDICAL_PRO_CELL_DETAIL_ENDPOINT_UNBUILT_01.
					days: [],
					score: 0,
					note: '',
					date: '',
					delayed: false,
				});
			}
			return {
				id: String(row.itemId),
				name: row.name ?? '',
				areaIndex,
				subGroup: '',
				frequency: cadence?.frequency ?? 'daily',
				frequencyLabel: cadence?.label ?? '',
			};
		}),
	}));
	return { treatmentBar: wire?.treatmentBar ?? null, areas, grid };
}

/**
 * A calendar date as `YYYY-MM-DD`, read off the LOCAL date.
 *
 * Deliberately not `toISOString()`, which converts to UTC first: Korea runs nine hours ahead, so local midnight
 * is still the previous day in UTC, and every week label on the chart would have been shifted a day earlier.
 */
function toISODate(d: Date): string {
	const month = String(d.getMonth() + 1).padStart(2, '0');
	const day = String(d.getDate()).padStart(2, '0');
	return `${d.getFullYear()}-${month}-${day}`;
}

/** Monday of the given 0-based grid week; empty when the patient has no discharge date. */
function getWeekStartISO(startDate: string | null, weekIndex: number): string {
	if (!startDate) return '';
	const monday = new Date(`${startDate}T00:00:00`);
	// Week 1 opens on the MONDAY of the discharge week, so the columns line up with real calendar weeks —
	// the same rule the server uses, kept identical here so the two never label a week differently.
	monday.setDate(
		monday.getDate() - ((monday.getDay() + 6) % 7) + weekIndex * 7,
	);
	return toISODate(monday);
}

/** Sunday of the given 0-based grid week. */
function getWeekEndISO(startDate: string | null, weekIndex: number): string {
	const start = getWeekStartISO(startDate, weekIndex);
	if (!start) return '';
	const sunday = new Date(`${start}T00:00:00`);
	sunday.setDate(sunday.getDate() + 6);
	return toISODate(sunday);
}

/** Download PRO CSV for a patient — BE GET /pro-responses/csv (UTF-8 + BOM; meta header, empty grid today). */
export async function downloadPatientProCsv(
	patient: PatientListItem,
	_areaLabels?: string[],
): Promise<void> {
	const res = await httpClient.get(
		endpoints.patientProResponsesCsv(patient.uid),
		{
			responseType: 'blob',
		},
	);
	triggerBlobDownload(res.data as Blob, `pro_responses_${patient.uid}.csv`);
}

// ── Biometric data layer ──

export type { BiometricData };

/** Wire shape of BiometricSeriesResponse (BE-2 / FEATURE_020). Every series is independently empty-able. */
interface BiometricWire {
	steps: { date: string; value: number | null; goal: number | null }[] | null;
	sleep:
		| {
				date: string;
				value: number | null;
				deep: number | null;
				light: number | null;
				rem: number | null;
				// Sent by the server but no longer displayed anywhere on this screen. Declared because it is
				// really on the wire; deliberately not carried into BioSleep (see bio-data.ts).
				awake: number | null;
				manual: number | null;
				normalMin: number | null;
				normalMax: number | null;
		  }[]
		| null;
	heartRate:
		| { date: string; value: number | null; band: string | null }[]
		| null;
	summary: {
		avgSteps: number | null;
		avgSleepHours: number | null;
		avgRestingHr: number | null;
		hrAbnormalDays: number | null;
	} | null;
	lastSync: string | null;
	syncStale: boolean;
}

const HR_BANDS: readonly string[] = ['LOW', 'NORMAL', 'HIGH'];
const asBand = (b: string | null): HrBand | null =>
	b != null && HR_BANDS.includes(b) ? (b as HrBand) : null;

/**
 * Biometric series for a patient — BE-2 `GET /biometrics` (RBAC-guarded). Fetches the full history
 * (`period=all`); the tab slices client-side for instant period switching. Each series is independently
 * empty (a device may sync steps but no heart rate) — an empty list means "nothing recorded", never zero.
 */
export async function fetchBiometric(
	patient: PatientListItem,
): Promise<BiometricData> {
	const { data } = await httpClient.get<ApiSuccess<BiometricWire>>(
		endpoints.patientBiometrics(patient.uid),
		{ params: { period: 'all' } },
	);
	const w = data.data;
	const steps = Array.isArray(w?.steps) ? w.steps : [];
	const sleep = Array.isArray(w?.sleep) ? w.sleep : [];
	const heartRate = Array.isArray(w?.heartRate) ? w.heartRate : [];
	return {
		steps: steps
			.filter((p) => p.value != null)
			.map((p) => ({ date: p.date, value: p.value as number, goal: p.goal ?? null })),
		// Drop days with no sleep reading (value == null) rather than coercing to 0 —
		// matches steps/heartRate above and the "empty means nothing recorded, never zero"
		// contract. Zero-days otherwise skew the average and the 미충족/초과 counts, and the
		// chart can't tell a gap from a real 0-hour night (SHCT-566). Sub-stages keep ?? 0:
		// they're the per-day breakdown of a real reading.
		sleep: sleep
			.filter((p) => p.value != null)
			.map((p) => ({
				date: p.date,
				value: p.value as number,
				deep: p.deep ?? 0,
				light: p.light ?? 0,
				rem: p.rem ?? 0,
				manual: p.manual ?? 0,
				normalMin: p.normalMin ?? null,
				normalMax: p.normalMax ?? null,
			})),
		heartRate: heartRate
			.filter((p) => p.value != null)
			.map((p) => ({
				date: p.date,
				value: p.value as number,
				band: asBand(p.band),
			})),
		summary: {
			avgSteps: w?.summary?.avgSteps ?? null,
			avgSleepHours: w?.summary?.avgSleepHours ?? null,
			avgRestingHr: w?.summary?.avgRestingHr ?? null,
			hrAbnormalDays: w?.summary?.hrAbnormalDays ?? null,
		},
		lastSync: w?.lastSync ?? null,
		syncStale: w?.syncStale ?? false,
	};
}

/** Biometric CSV — BE-2 `GET /biometrics/csv` (UTF-8; steps/sleep/RHR columns, empty grid until samples sync). */
export async function downloadPatientBioCsv(
	patient: PatientListItem,
	period?: string,
): Promise<void> {
	// PRD §3.3: CSV = currently active period slice. The selected period is forwarded so the file
	// matches the on-screen 7/30/90/all tab (BE must honor `period`; see SHCT-344 BE notes).
	const res = await httpClient.get(
		endpoints.patientBiometricsCsv(patient.uid),
		{
			responseType: 'blob',
			params: period ? { period } : undefined,
		},
	);
	triggerBlobDownload(res.data as Blob, `biometrics_${patient.uid}.csv`);
}

// ── Intervention data layer (BE: GET /patients/{uid}/interventions + /csv) ──

export type { InterventionItem };

/**
 * Wire status → the value {@link InterventionItem} carries. Unknown values fall back to `scheduled`
 * so a status added server-side shows as outstanding rather than silently as done.
 */
const SCHEDULE_STATUS_ON_WIRE: Record<string, InterventionItem['status']> = {
	scheduled: 'scheduled',
	completed: 'completed',
	no_show: 'noshow',
};

// BE wire row (InterventionTimelineItem).
interface InterventionWire {
	id: string;
	kind: 'sched' | 'note';
	status: string; // scheduled | completed | no_show  (BE Schedule.status, V119 — lower-cased on the wire)
	dt: string;
	type: string | null; // VISIT/INPERSON/CALL/FU (sched) or MESSAGE/CALL (note)
	title: string | null;
	body: string | null;
	/** Schedule's raw 목적 (purpose), separate from `body`'s combined purpose+memo display text; null for a note. */
	purpose: string | null;
	staffId: number | null;
	staffName: string | null;
	staffRole: string | null; // DOCTOR/NURSE/CRC
	channel: string | null; // SMS/APP/SMS_APP
	callDirection: string | null; // 방향 — OUT (발신) | IN (수신)
	callDurationMin: number | null;
	target: string | null; // SELF/GUARDIAN
	sharedManual: boolean | null;
}

const SCHED_TYPE_LABEL: Record<string, string> = {
	VISIT: '외래',
	INPERSON: '대면',
	CALL: '전화',
	FU: '재방문',
};
const NOTE_TYPE_LABEL: Record<string, string> = {
	MESSAGE: '메시지',
	CALL: '전화',
};

function roleKo(role: string | null): string {
	if (role === 'DOCTOR') return '의사';
	if (role === 'NURSE') return '간호사';
	if (role === 'CRC') return 'CRC';
	return role ?? '';
}

async function fetchInterventionWire(
	uid: string,
	params?: { status?: string; period?: string },
): Promise<InterventionWire[]> {
	const { data } = await httpClient.get<ApiSuccess<InterventionWire[]>>(
		endpoints.patientInterventions(uid),
		{ params },
	);
	return data.data;
}

/**
 * The bare numeric id the edit/detail calls take, recovered from the BE's `sched-4` / `note-4`.
 *
 * **The result is deliberately NOT unique across the timeline** — the prefix is exactly what made it so,
 * because the number alone is only unique within its own table. A schedule and a note that happen to
 * share a row number both strip to `4`. Anything identifying a ROW must use `rowKey` (the unprefixed id
 * kept verbatim); this is only for addressing a record in its own API. (SHCT-809)
 */
function stripInterventionId(raw: string, kind: string): string {
	const prefix = `${kind}-`;
	return raw.startsWith(prefix) ? raw.slice(prefix.length) : raw;
}

/** The separator the BE joins a schedule's 목적 and 메모 with when it builds `body` for the timeline. */
const SCHEDULE_BODY_JOIN = ' / ';

/**
 * A schedule row's raw 메모, recovered from the combined `body` the BE sends (SHCT-788).
 *
 * The BE builds `body` as `목적` alone, or `목적 + " / " + 메모` when a memo exists, and sends `purpose`
 * beside it but no `memo`. The edit modal has to prefill BOTH fields, so before this it prefilled 메모
 * from `body` — the combined text — and saving wrote that whole string back into 메모. The next render
 * then re-joined the purpose onto a memo that already contained the previous purpose, so every edit
 * stacked one more 목적 into the 내용 column, permanently: the accumulated value is what got stored.
 *
 * Recovering it here rather than at the modal keeps the knowledge of the BE's join in the one file that
 * already documents it. A note row has no purpose/memo split at all and yields undefined.
 *
 * The right long-term shape is for the BE to send `memo` as its own wire field, exactly as it already
 * does for `purpose` — then this can go. Until then the split is exact, because the join is exact.
 */
function memoOf(r: InterventionWire): string | undefined {
	if (r.kind !== 'sched') return undefined;
	const body = r.body ?? '';
	const purpose = r.purpose ?? '';
	if (!purpose) return body || undefined;
	const joined = purpose + SCHEDULE_BODY_JOIN;
	if (body.startsWith(joined)) return body.slice(joined.length) || undefined;
	// `body` is the purpose alone (no memo), or a shape this join does not explain — either way there
	// is no memo we can name, and guessing one would put text in the clinician's memo field.
	return undefined;
}

/** Intervention timeline rows for a patient (예정 schedules + 완료 notes/past).
 * Filters are forwarded to the BE — omit to fetch everything. */
export async function fetchInterventionItems(
	patient: PatientListItem,
	filters?: { status?: string; period?: string },
): Promise<InterventionItem[]> {
	const params: Record<string, string> = {};
	if (filters?.status && filters.status !== 'ALL')
		params.status = filters.status;
	if (filters?.period && filters.period !== 'ALL')
		params.period = filters.period;
	const rows = await fetchInterventionWire(patient.uid, params);
	return rows.map((r) => ({
		kind: r.kind,
		// Three values travel, not two: the server has sent `no_show` since V119 and PRD 04 v2.19
		// §3.4 [MED.016] gives it its own 미완료 badge. Collapsing it into `scheduled` (what this
		// line used to do) showed a missed appointment as still upcoming.
		status: SCHEDULE_STATUS_ON_WIRE[r.status] ?? 'scheduled',
		dt: r.dt,
		typeLabel:
			(r.kind === 'sched' ? SCHED_TYPE_LABEL : NOTE_TYPE_LABEL)[r.type ?? ''] ??
			r.type ??
			'',
		rawType: r.type ?? '',
		title: r.title ?? '',
		text: r.body ?? '',
		purpose: r.purpose ?? undefined,
		memo: memoOf(r),
		staff: r.staffName ?? '',
		staffRole: roleKo(r.staffRole),
		id: stripInterventionId(r.id, r.kind),
		// SHCT-809 — the server's id is already unique across both kinds; keep it, because the stripped
		// one is not (see `stripInterventionId`).
		rowKey: r.id,
	}));
}

/** Consultation notes (the note-kind timeline rows) for a patient, for the intervention panel popover. */
export async function fetchConsultationNotes(
	patientId: string,
): Promise<ConsultationNote[]> {
	const rows = await fetchInterventionWire(patientId);
	return rows
		.filter((r) => r.kind === 'note')
		.map((r) => ({
			id: stripInterventionId(r.id, r.kind),
			patientId,
			date: r.dt.slice(0, 10),
			time: r.dt.length >= 16 ? r.dt.slice(11, 16) : '',
			type: r.type === 'MESSAGE' ? 'message' : 'call',
			title: r.title ?? '',
			text: r.body ?? '',
			staff: r.staffName ?? '',
			// SHCT-865 — the wire has always carried the authoring staff id; it was
			// dropped here, which is why the panel could not tell whose note it was.
			staffId: r.staffId ?? undefined,
			staffRole:
				r.staffRole === 'DOCTOR'
					? '의사'
					: r.staffRole === 'CRC'
						? 'CRC'
						: '간호사',
			consultationTarget: r.target === 'GUARDIAN' ? 'guardian' : 'patient',
			messageChannels:
				r.channel === 'SMS_APP'
					? ['APP', 'SMS']
					: r.channel === 'SMS'
						? ['SMS']
						: r.channel === 'APP'
							? ['APP']
							: undefined,
			callDirection:
				r.callDirection === 'OUT' || r.callDirection === 'IN'
					? r.callDirection
					: undefined,
			callDuration: r.callDurationMin ?? undefined,
		}));
}

/** The two toolbar filters the intervention timeline and its CSV export share. */
export interface InterventionFilters {
	/**
	 * 상태 필터 — ALL keeps every group.
	 *
	 * SHCT-599 · SHCT-876 — three groups travel, not two: `NO_SHOW` (미완료) is a past appointment with no
	 * intervention against it. The CSV endpoint is built from the same `timeline()` call as the table, so
	 * this list must stay identical to the toolbar's, or the file would silently hold different rows.
	 */
	status: 'ALL' | 'SCHEDULED' | 'COMPLETED' | 'NO_SHOW';
	/** 기간 필터 — a window measured back from now; upcoming rows always survive it. */
	period: 'ALL' | 'LAST_1_MONTH' | 'LAST_3_MONTHS';
}

/**
 * Download intervention CSV for a patient — BE GET /interventions/csv (UTF-8 + BOM).
 *
 * The filters the user has selected are passed to the server, so the file holds the rows on screen rather
 * than the whole history.
 */
export async function downloadPatientInterventionCsv(
	patient: PatientListItem,
	filters: InterventionFilters,
): Promise<void> {
	const res = await httpClient.get(
		endpoints.patientInterventionsCsv(patient.uid),
		{
			params: { status: filters.status, period: filters.period },
			responseType: 'blob',
		},
	);
	triggerBlobDownload(res.data as Blob, `interventions_${patient.uid}.csv`);
}

// ── Consultation manuals (BE: GET /consultation-manual + /search) ──

export type { ConsultationNote };

/** Grade CODE on the wire; the clinician reads 단계 1/2/3, which the pane resolves from this code. */
export type ManualGradeCode = 'G1' | 'G2' | 'G3';

/** One authored content block (text/image/video), as stored by the admin block editor. */
export interface ManualBlock {
	type: 'text' | 'image' | 'video';
	title: string;
	/** text blocks: sanitized rich-text HTML; image/video: optional caption (shown when showText). */
	text: string;
	showText: boolean;
	/** image blocks: the stored image URL. */
	dataUrl?: string | null;
	/** video blocks: the external video URL (YouTube/Vimeo). */
	url?: string | null;
}

/** One grade tab of an 항목. A grade nobody has authored is simply absent from an item's `grades`. */
export interface ManualGrade {
	grade: ManualGradeCode;
	/** Display code of the manual behind this tab. */
	cid: string;
	title: string;
	/** The body as sanitized HTML. Text-only fallback — the save-time sanitizer strips <img>/<iframe>,
	 * so media is rendered from `blocks` instead (see ManualPane). */
	content: string;
	/** The authored block array; text carries sanitized HTML, image/video carry the media refs the
	 * sanitized `content` cannot. Absent on older payloads → render falls back to `content`. */
	blocks?: ManualBlock[];
}

/** One 항목 — a PRO leaf item and the manual content published against it. */
export interface ManualItem {
	/** PRO leaf id — how an item is matched to a patient's PRO response. */
	proItemId: number;
	itemName: string;
	/** Display code + title of the representative manual (the lowest published grade). */
	cid: string;
	title: string;
	/** Derived plain-text excerpt; the master stores no summary column. */
	summary: string;
	grades: ManualGrade[];
}

export interface ManualSubArea {
	subArea: string;
	items: ManualItem[];
}

export interface ManualArea {
	area: string;
	subAreas: ManualSubArea[];
}

/** 영역 > 하위영역 > 항목, exactly as the server assembled it. */
export type ManualTree = ManualArea[];

/** The wire: BE ManualTreeResponse. */
interface ManualTreeWire {
	areas: ManualArea[] | null;
}

/**
 * The consultation-manual tree (영역 > 하위영역 > 항목).
 *
 * The server does the assembling: the admin master stores one row per grade, and the clinician reads one row per
 * 항목 with three grade tabs, so the grouping and the 영역/하위영역/항목 names (which come from the PRO item
 * master) are resolved server-side and arrive ready to render.
 */
export async function fetchManualTree(): Promise<ManualTree> {
	const { data } = await httpClient.get<ApiSuccess<ManualTreeWire>>(
		endpoints.consultationManual,
	);
	return data.data.areas ?? [];
}

/**
 * Search the manuals — BE `GET /consultation-manual/search?q=`.
 *
 * Returns the SAME tree, filtered to the 항목 matching every keyword: a search narrows the tree the clinician is
 * reading rather than swapping the pane to a flat list. Every keyword must match (they narrow, not widen), and
 * the server matches across 제목·본문·영역·하위영역·항목명·요약·코드.
 */
export async function searchPatientManuals(query: string): Promise<ManualTree> {
	const { data } = await httpClient.get<ApiSuccess<ManualTreeWire>>(
		endpoints.consultationManualSearch,
		{ params: { q: query } },
	);
	return data.data.areas ?? [];
}
